import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Animal3DCanvas } from "./Animal3DCanvas";
import type { AnimalType } from "./Animal3DModel";

const ANIMAL_LIST: Array<{ id: AnimalType; name: string; emoji: string; greeting: string }> = [
  { id: "bunny", name: "うさぎ先生", emoji: "🐰", greeting: "いっしょに日本語を学ぼう！🐾" },
  { id: "frog", name: "カエル書生", emoji: "🐸", greeting: "ケロケロ！学問の道は奥深いであります！" },
  { id: "tanuki", name: "タヌキ旅人", emoji: "🍃", greeting: "ポンポコ！お茶でも飲んでゆっくりしてや〜" },
  { id: "kitsune", name: "キツネ巫女", emoji: "🦊", greeting: "コンコン！良きご縁がありますように⛩️" },
  { id: "monkey", name: "サル飛脚", emoji: "🐒", greeting: "ウキッ！元気いっぱいに届けるでござる！" },
];

export function Animal3DWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState<AnimalType>("bunny");
  const [message, setMessage] = useState<string>("");
  const [heartCount, setHeartCount] = useState(0);

  const current = ANIMAL_LIST.find((a) => a.id === selectedAnimal) ?? ANIMAL_LIST[0];

  function handleJump() {
    setHeartCount((c) => c + 1);
    setMessage(current.greeting);
    setTimeout(() => setMessage(""), 3000);
  }

  return (
    <div className="fixed bottom-5 left-5 z-40 select-none">
      {/* 展開された3Dパークカード */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 30 }}
            transition={{ type: "spring", damping: 18 }}
            className="mb-3 w-80 sm:w-96 bg-[#fffdf7]/95 backdrop-blur-lg rounded-3xl p-4 shadow-2xl border-2 border-[#d9b45f]/40 flex flex-col"
            style={{
              boxShadow: "0 12px 36px rgba(60, 20, 0, 0.22)",
            }}
          >
            {/* ヘッダー */}
            <div className="flex items-center justify-between pb-2 border-b border-[#d9b45f]/20">
              <div className="flex items-center gap-2">
                <span className="text-xl">{current.emoji}</span>
                <div>
                  <h3 className="text-sm font-black text-[#8f332c] leading-tight">
                    {current.name} (3D)
                  </h3>
                  <p className="text-[10px] text-stone-500 font-bold">
                    島のおともだちとふれあおう
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600 flex items-center justify-center text-xs font-bold transition"
              >
                ✕
              </button>
            </div>

            {/* どうぶつ切り替えタブ */}
            <div className="grid grid-cols-5 gap-1 mt-2.5 bg-stone-100/80 p-1 rounded-2xl">
              {ANIMAL_LIST.map((a) => (
                <button
                  key={a.id}
                  onClick={() => {
                    setSelectedAnimal(a.id);
                    setMessage(a.greeting);
                    setTimeout(() => setMessage(""), 3000);
                  }}
                  className={`py-1.5 rounded-xl text-xs font-black transition flex flex-col items-center gap-0.5 ${
                    selectedAnimal === a.id
                      ? "bg-white text-[#8f332c] shadow-sm border border-[#d9b45f]/30"
                      : "text-stone-500 hover:text-stone-800"
                  }`}
                >
                  <span className="text-base">{a.emoji}</span>
                  <span className="text-[9px] truncate w-full px-1">{a.name}</span>
                </button>
              ))}
            </div>

            {/* ふきだしセリフ */}
            <div className="relative min-h-[44px] flex items-center justify-center my-2">
              <AnimatePresence mode="wait">
                <motion.div
                  key={message || "default"}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="bg-white/95 border border-[#d9b45f]/30 rounded-2xl px-3.5 py-1.5 shadow-sm text-center"
                >
                  <p className="text-xs font-black text-[#273f7a]">
                    {message || "「ボクをつついてみてね！🐾」"}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* 3Dキャンバス本体 */}
            <div className="rounded-2xl overflow-hidden bg-gradient-to-b from-sky-50/50 via-white to-amber-50/60 border border-[#d9b45f]/20">
              <Animal3DCanvas
                animalType={selectedAnimal}
                height={260}
                onJump={handleJump}
              />
            </div>

            {/* フッターアクション */}
            <div className="mt-3 flex items-center justify-between text-xs font-bold text-stone-600 px-1">
              <span className="flex items-center gap-1 text-[#e05a47]">
                <span>💖 なかよし度:</span>
                <span className="font-black text-sm">{heartCount}</span>
              </span>
              <span className="text-[10px] text-stone-400">
                ドラッグで回転
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* フローティング起動ボタン */}
      <motion.button
        type="button"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center gap-2 bg-[#fffdf7] border-2 border-[#d9b45f] px-3.5 py-2.5 rounded-full shadow-lg hover:shadow-xl text-[#8f332c] font-black text-xs sm:text-sm transition group"
        style={{
          boxShadow: "0 6px 20px rgba(60, 20, 0, 0.18)",
        }}
      >
        <motion.span
          animate={{
            rotate: [0, 15, -15, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="text-xl"
        >
          {current.emoji}
        </motion.span>
        <span>3Dどうぶつ</span>
        <span className="bg-[#8f332c] text-white text-[10px] px-1.5 py-0.5 rounded-full">
          {isOpen ? "閉じる" : "あそぶ"}
        </span>
      </motion.button>
    </div>
  );
}
